import type { Totals } from '../game/match';
import { SEVEN_GUAA_CLIP, SEVEN_GUAA_MILESTONE } from '../lib/voice';
import type { Teams } from '../shared/schemas';

function playMilestoneClip(e: React.MouseEvent) {
  e.stopPropagation();
  new Audio(SEVEN_GUAA_CLIP).play().catch(() => {});
}

export function Scoreboard({ teams, t, compact = false }: { teams: Teams; t: Totals; compact?: boolean }) {
  const label = (id: 'A' | 'B') => {
    const team = teams[id];
    const align = id === 'A' ? 'justify-start text-left' : 'justify-end text-right';
    return (
      <span className={`flex min-w-0 items-center gap-1.5 ${align} ${id === 'A' ? '' : 'flex-row-reverse'}`}>
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: team.color }} aria-hidden />
        <span className="truncate text-xs font-bold">{team.name}</span>
      </span>
    );
  };

  const number = (id: 'A' | 'B') => {
    const team = teams[id];
    const guaa = id === 'A' ? t.guaaA : t.guaaB;
    const milestone = guaa === SEVEN_GUAA_MILESTONE;
    return (
      <span className="flex items-center gap-1">
        {milestone ? (
          <span
            className={`animate-guaa-stamp font-display font-extrabold tabular-nums ${compact ? 'text-4xl' : 'text-5xl'} leading-none bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(251,191,36,0.65)]`}
          >
            {guaa}
          </span>
        ) : (
          <span className={`font-display font-extrabold tabular-nums ${compact ? 'text-4xl' : 'text-5xl'} leading-none`} style={{ color: team.color }}>
            {guaa}
          </span>
        )}
        {milestone && (
          <button
            type="button"
            onClick={playMilestoneClip}
            aria-label={`Play the ${SEVEN_GUAA_MILESTONE} Guaa sound for ${team.name}`}
            className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-amber-400/20 text-[11px] ring-1 ring-amber-400/40 hover:bg-amber-400/30 active:scale-95"
          >
            🔊
          </button>
        )}
      </span>
    );
  };

  const stats = (id: 'A' | 'B') => {
    const align = id === 'A' ? 'text-left' : 'text-right';
    const called = id === 'A' ? t.calledA : t.calledB;
    const saved = id === 'A' ? t.savedA : t.savedB;
    const courts = id === 'A' ? t.courtsA : t.courtsB;
    return (
      <span className={`min-w-0 truncate text-[11px] text-ink-soft/80 ${align}`}>
        {saved}/{called} saved
        {courts > 0 && ` · ${courts} court${courts > 1 ? 's' : ''}`}
      </span>
    );
  };

  return (
    <div role="group" aria-label={`Guaa: ${teams.A.name} ${t.guaaA}, ${teams.B.name} ${t.guaaB}. Fewest wins.`}>
      <div className="relative flex items-center justify-between gap-2">
        {label('A')}
        <span className="eyebrow absolute left-1/2 -translate-x-1/2 text-[9px] whitespace-nowrap text-ink-soft/60" aria-hidden>
          Guaa
        </span>
        {label('B')}
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-x-2">
        <span className="flex justify-end pr-2">{number('A')}</span>
        <span className="font-display text-xl font-bold text-ink-soft/30" aria-hidden>
          |
        </span>
        <span className="flex justify-start pl-2">{number('B')}</span>

        {stats('A')}
        <span aria-hidden />
        {stats('B')}
      </div>
    </div>
  );
}
